import { useCallback, useEffect, useRef, useState } from "react";

const TEST_SOUND_SRC = "/audio/beZonedVolumeTest.mp3";
const SINK_ID_NOT_SUPPORTED_ERROR =
  "This browser does not support selecting a specific speaker output.";

type AudioElementWithSinkId = HTMLAudioElement & {
  setSinkId: (sinkId: string) => Promise<void>;
};

export interface SpeakerTestSoundVM {
  isPlaying: boolean;
  togglePlay: () => Promise<void>;
  sinkError: string | null;
}

export const useSpeakerTestSound = (
  selectedSpeakerId: string | null
): SpeakerTestSoundVM => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const selectedSpeakerIdRef = useRef(selectedSpeakerId);
  const sinkApplyQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const [isPlaying, setIsPlaying] = useState(false);
  const [sinkError, setSinkError] = useState<string | null>(null);

  selectedSpeakerIdRef.current = selectedSpeakerId;

  const applySinkId = useCallback(
    async (audio: HTMLAudioElement, speakerId: string | null) => {
      const apply = async () => {
        if (!("setSinkId" in audio)) {
          if (speakerId && speakerId !== "default") {
            throw new Error(SINK_ID_NOT_SUPPORTED_ERROR);
          }
          return;
        }

        await (audio as AudioElementWithSinkId).setSinkId(speakerId ?? "");
      };

      const queuedApply = sinkApplyQueueRef.current.then(apply, apply);
      sinkApplyQueueRef.current = queuedApply.then(
        () => null,
        () => null
      );
      await queuedApply;
    },
    []
  );

  useEffect(() => {
    const audio = new Audio(TEST_SOUND_SRC);
    audio.loop = false;
    audioRef.current = audio;

    const onPlay = () => {
      isPlayingRef.current = true;
      setIsPlaying(true);
    };

    const onPause = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
    };

    const onEnded = () => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      audio.currentTime = 0;
    };

    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.removeAttribute("src");
      audioRef.current = null;
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    setSinkError(null);

    if (isPlayingRef.current && audio) {
      audio.pause();
      audio.currentTime = 0;
    }

    if (!audio) {
      return;
    }

    applySinkId(audio, selectedSpeakerId).catch((error: unknown) => {
      if (selectedSpeakerIdRef.current !== selectedSpeakerId) {
        return;
      }
      setSinkError(
        error instanceof Error
          ? error.message
          : "Failed to set audio output device"
      );
    });
  }, [applySinkId, selectedSpeakerId]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (isPlayingRef.current) {
      audio.pause();
      audio.currentTime = 0;
    } else {
      setSinkError(null);
      try {
        // Apply within the click handler and wait for it. This prevents test
        // playback from racing a speaker change and falling back to default.
        await applySinkId(audio, selectedSpeakerIdRef.current);
        await audio.play();
      } catch (error) {
        setSinkError(
          error instanceof Error ? error.message : "Failed to play test sound"
        );
      }
    }
  }, [applySinkId]);

  return { isPlaying, sinkError, togglePlay };
};
